import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, CircularProgress, Modal, Typography } from '@mui/material';

import asyncService from '../../../../../../datahub/asyncService';
import WaeButton, { BUTTON_VARIANT } from '../../../../../components/Button';
import selectUser from '../../../../../store/selectors/appSelector';
import { BLACK, PRIMARY_COLOR } from '../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../theme/fontConstants';
import { epochToDateInReadableFormat } from '../../../../../utils';
import { showSnackbar } from '../../../../app';
import { capitalizeFirstLetter } from '../../../UsersHelpers';

const styles = {
  modal: {
    position: 'relative',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '16px',
    bgcolor: 'background.paper',
    border: '1px solid #CCCCD6',
    boxShadow: 24,
    p: 4,
    maxHeight: 577,
    width: 470,
  },
  header: {
    fontSize: 20,
    fontFamily: PRIMARY_FONT[700],
    color: BLACK[100],
  },
  subHeader: {
    fontSize: 16,
    fontFamily: PRIMARY_FONT[300],
    color: BLACK[100],
  },
  industryTitle: {
    fontSize: 20,
    fontFamily: PRIMARY_FONT[300],
    color: BLACK[100],
  },
  industryName: {
    fontSize: 20,
    fontFamily: PRIMARY_FONT[700],
    color: BLACK[100],
  },
  corpAndPositionName: {
    fontSize: 16,
    fontFamily: PRIMARY_FONT[700],
    color: BLACK[100],
  },
  date: {
    fontSize: 16,
    fontFamily: PRIMARY_FONT[300],
    color: BLACK[100],
    fontStyle: 'italic',
  },
};

const categorizeExperiencesByIndustry = (workerExperiences = []) => {
  const industryMap = {};

  workerExperiences.forEach((exp) => {
    const { industry, ...rest } = exp;
    if (!industryMap[industry]) {
      industryMap[industry] = [];
    }
    industryMap[industry].push(rest);
  });

  return Object.entries(industryMap).map(([industry, exp]) => ({
    industry,
    experiences: exp,
  }));
};

const Experience = ({ jobType = '', corpName = '', startDate, endDate }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography sx={styles.corpAndPositionName}>{`${jobType}, ${corpName}`}</Typography>
    </Box>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography sx={styles.date}>
        {LanguageConverterV2('userDetail.experience.seeExperience.to', {
          startDate: epochToDateInReadableFormat(startDate),
          endDate: epochToDateInReadableFormat(endDate),
        })}
      </Typography>
    </Box>
  </Box>
);

Experience.propTypes = {
  jobType: PropTypes.string,
  corpName: PropTypes.string,
  startDate: PropTypes.number,
  endDate: PropTypes.number,
};

const WorkExperienceModal = ({ onClose, selectedUserId }) => {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const { token, refreshToken } = useSelector(selectUser);
  const payload = useMemo(
    () => ({
      candidateId: selectedUserId,
    }),
    [selectedUserId]
  );

  useEffect(() => {
    const getSetUserWorkExperiences = () => {
      setLoading(true);

      asyncService({
        httpMethod: 'POST',
        route: 'users/get-candidate-experience',
        data: payload,
        onSuccess: (d) => {
          const webResponse = d?.data || [];
          setExperiences(categorizeExperiencesByIndustry(webResponse?.experience || []));
          setLoading(false);
        },
        onError: () => {
          dispatch(showSnackbar({ message: 'userDetail.experience.errorGettingExperience' }));
          setLoading(false);
        },
        dispatch,
        token,
        refreshToken,
      });
    };

    getSetUserWorkExperiences();
  }, [dispatch, token, refreshToken, payload]);

  return (
    <Modal open onClose={onClose} aria-labelledby="work-experience-modal">
      <Box sx={styles.modal}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxHeight: 450,
                overflowY: 'auto',
                pr: 1,
              }}
            >
              <Typography sx={styles.header}>
                {LanguageConverterV2('userDetail.experience.header')}
              </Typography>
              <Typography sx={styles.subHeader}>
                {LanguageConverterV2('userDetail.experience.info')}
              </Typography>

              {experiences?.length > 0 ? (
                experiences.map((item) => (
                  <Box
                    key={item?.industry}
                    sx={{ mt: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography sx={styles.industryTitle}>
                        {LanguageConverterV2('userDetail.experience.industrySelected')}
                      </Typography>
                      <Typography sx={styles.industryName}>
                        {capitalizeFirstLetter(item?.industry)}
                      </Typography>
                    </Box>
                    {item?.experiences?.map((e) => (
                      <Experience
                        key={e?.experienceId}
                        jobType={e?.jobType}
                        corpName={e?.companyName}
                        startDate={e?.start}
                        endDate={e?.end}
                      />
                    ))}
                  </Box>
                ))
              ) : (
                <Typography sx={{ ...styles.header, mt: 4, mb: 4 }}>
                  {LanguageConverterV2('userDetail.experience.noExperience')}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '50px',
                background: 'linear-gradient(to top, white, rgba(255,255,255,0))',
                pointerEvents: 'none',
              }}
            />
          </Box>
        )}

        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
          <WaeButton
            variant={BUTTON_VARIANT.DEFAULT}
            actionColor={PRIMARY_COLOR[70]}
            onClick={onClose}
          >
            {LanguageConverterV2('buttonText.ok')}
          </WaeButton>
        </Box>
      </Box>
    </Modal>
  );
};

WorkExperienceModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  selectedUserId: PropTypes.string.isRequired,
};

export default WorkExperienceModal;
